import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  PlatformUrls,
  UploadedFile,
  AutomationStage,
  StageStatus,
  AutomationError,
} from '@/types';
import type { VoiceNote } from '@/components/common';
import { VALID_PLATFORM_KEYS, toFrontendKey } from '@/constants/platforms';
import { NICHE_MAX_LENGTH } from '@/features/onboarding/constants/niches';

// --- State shape ---

interface NicheState {
  // 1-3 niches (LIL-426): library ids or the user's own text. Empty when unset.
  selected: string[];
  searchQuery: string;
}

interface GoalsState {
  ranked: string[];
}

interface FilesState {
  uploaded: UploadedFile[];
  storageUsed: number;
  storageQuota: number;
}

interface VoiceNotesState {
  recorded: VoiceNote[];
}

interface ContentState {
  // Selected topic names (curated suggestions + any custom-typed topics).
  selectedTopics: string[];
  // Content mix percentages; must total 100 before the step can be saved.
  mix: { personal: number; market: number; industry: number };
}

interface AutomationState {
  isRunning: boolean;
  currentStage: AutomationStage | null;
  stages: StageStatus[];
  startedAt: string | null;
  error: AutomationError | null;
}

interface SocketState {
  isConnected: boolean;
  connectionError: string | null;
}

// --- Combined store types ---

interface OnboardingState {
  niche: NicheState;
  platforms: PlatformUrls;
  goals: GoalsState;
  files: FilesState;
  voiceNotes: VoiceNotesState;
  content: ContentState;
  currentStep: number;
  completedSteps: number[];
  skippedSteps: number[];
  automation: AutomationState;
  socket: SocketState;
}

// --- Actions ---

interface OnboardingActions {
  // Niche
  setNiche: (niche: string[]) => void;
  setNicheSearchQuery: (query: string) => void;

  // Platforms
  setPlatformUrl: (platform: keyof PlatformUrls, url: string) => void;

  // Goals
  setGoals: (goals: string[]) => void;

  // Content (topics + mix)
  setSelectedTopics: (topics: string[]) => void;
  setContentMix: (mix: ContentState['mix']) => void;

  // Files
  addFile: (file: UploadedFile) => void;
  removeFile: (fileId: string) => void;
  updateFileProgress: (fileId: string, progress: number, status: UploadedFile['status']) => void;
  setStorageQuota: (quota: number) => void;
  setStorageUsed: (used: number) => void;

  // Voice notes
  addVoiceNote: (note: VoiceNote) => void;
  updateVoiceNote: (noteId: string, patch: Partial<VoiceNote>) => void;
  removeVoiceNote: (noteId: string) => void;

  // Steps
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  markStepCompleted: (step: number) => void;

  // Skipped steps
  addSkippedStep: (step: number) => void;
  removeSkippedStep: (step: number) => void;
  clearSkippedSteps: () => void;

  // Socket
  setSocketConnected: (value: boolean) => void;
  setSocketError: (error: string | null) => void;

  // Automation
  startAutomation: () => void;
  markAutomationRunning: () => void;
  setAutomationStartedAt: (startedAt: string | null) => void;
  updateAutomationStage: (stage: StageStatus) => void;
  setAutomationError: (error: AutomationError | null) => void;
  completeAutomation: () => void;

  // Reset
  reset: () => void;
}

// --- Initial values ---

export const TOTAL_STEPS = 5;

// Default content mix shown on the topics step; mirrors the BE content_dna defaults.
const DEFAULT_CONTENT_MIX = { personal: 40, market: 30, industry: 30 };

const initialAutomationStages: StageStatus[] = [
  { stage: 'analyzing_niche', status: 'pending' },
  { stage: 'indexing_knowledge_base', status: 'pending' },
  { stage: 'understanding_goals', status: 'pending' },
  { stage: 'calibrating_tone', status: 'pending' },
  { stage: 'finalizing_setup', status: 'pending' },
];

/**
 * The stage a run is sitting on, derived purely from live stage data: the one
 * in flight, else the next one queued, else null. Keeps `automation.currentStage`
 * honest when `markAutomationRunning` relights a run whose slice was wiped by a
 * reload — `startAutomation` is the only other writer and it always knows the
 * answer, because it starts the run itself.
 */
function deriveCurrentStage(stages: StageStatus[]): AutomationStage | null {
  return (
    stages.find(s => s.status === 'processing')?.stage ??
    stages.find(s => s.status === 'pending')?.stage ??
    null
  );
}

// 10 GB — safe default so uploads aren't blocked before the API quota call resolves
const DEFAULT_STORAGE_QUOTA = 10 * 1024 * 1024 * 1024;

const initialState: OnboardingState = {
  niche: { selected: [], searchQuery: '' },
  platforms: {},
  goals: { ranked: [] },
  files: { uploaded: [], storageUsed: 0, storageQuota: DEFAULT_STORAGE_QUOTA },
  voiceNotes: { recorded: [] },
  content: { selectedTopics: [], mix: { ...DEFAULT_CONTENT_MIX } },
  currentStep: 1,
  completedSteps: [],
  skippedSteps: [],
  automation: {
    isRunning: false,
    currentStage: null,
    stages: initialAutomationStages.map(s => ({ ...s })),
    startedAt: null,
    error: null,
  },
  socket: { isConnected: false, connectionError: null },
};

// --- Store ---

export const useOnboardingStore = create<OnboardingState & OnboardingActions>()(
  devtools(
    persist(
      set => ({
        ...initialState,

        // Niche
        setNiche: niche =>
          set(
            state => ({ niche: { ...state.niche, selected: niche } }),
            false,
            'onboarding/setNiche'
          ),

        setNicheSearchQuery: query =>
          set(
            state => ({ niche: { ...state.niche, searchQuery: query } }),
            false,
            'onboarding/setNicheSearchQuery'
          ),

        // Platforms
        setPlatformUrl: (platform, url) =>
          set(
            state => ({
              platforms: { ...state.platforms, [platform]: url === '' ? undefined : url },
            }),
            false,
            'onboarding/setPlatformUrl'
          ),

        // Goals
        setGoals: goals => set({ goals: { ranked: goals } }, false, 'onboarding/setGoals'),

        // Content (topics + mix)
        setSelectedTopics: topics =>
          set(
            state => ({ content: { ...state.content, selectedTopics: topics } }),
            false,
            'onboarding/setSelectedTopics'
          ),

        setContentMix: mix =>
          set(state => ({ content: { ...state.content, mix } }), false, 'onboarding/setContentMix'),

        // Files
        addFile: file =>
          set(
            state => {
              if (state.files.uploaded.some(f => f.id === file.id)) return state;
              return {
                files: {
                  ...state.files,
                  uploaded: [...state.files.uploaded, file],
                  storageUsed: state.files.storageUsed + file.size,
                },
              };
            },
            false,
            'onboarding/addFile'
          ),

        removeFile: fileId =>
          set(
            state => {
              const removed = state.files.uploaded.find(f => f.id === fileId);
              return {
                files: {
                  ...state.files,
                  uploaded: state.files.uploaded.filter(f => f.id !== fileId),
                  storageUsed: Math.max(0, state.files.storageUsed - (removed?.size ?? 0)),
                },
              };
            },
            false,
            'onboarding/removeFile'
          ),

        updateFileProgress: (fileId, progress, status) =>
          set(
            state => ({
              files: {
                ...state.files,
                uploaded: state.files.uploaded.map(f =>
                  f.id === fileId ? { ...f, progress, status } : f
                ),
              },
            }),
            false,
            'onboarding/updateFileProgress'
          ),

        setStorageQuota: quota =>
          set(
            state => ({
              files: { ...state.files, storageQuota: quota },
            }),
            false,
            'onboarding/setStorageQuota'
          ),

        setStorageUsed: used =>
          set(
            state => ({
              files: { ...state.files, storageUsed: used },
            }),
            false,
            'onboarding/setStorageUsed'
          ),

        // Voice notes
        addVoiceNote: note =>
          set(
            state => {
              if (state.voiceNotes.recorded.some(n => n.id === note.id)) return state;
              return {
                voiceNotes: { recorded: [note, ...state.voiceNotes.recorded] },
              };
            },
            false,
            'onboarding/addVoiceNote'
          ),

        updateVoiceNote: (noteId, patch) =>
          set(
            state => ({
              voiceNotes: {
                recorded: state.voiceNotes.recorded.map(n =>
                  n.id === noteId ? { ...n, ...patch } : n
                ),
              },
            }),
            false,
            'onboarding/updateVoiceNote'
          ),

        removeVoiceNote: noteId =>
          set(
            state => ({
              voiceNotes: {
                recorded: state.voiceNotes.recorded.filter(n => n.id !== noteId),
              },
            }),
            false,
            'onboarding/removeVoiceNote'
          ),

        // Steps
        nextStep: () =>
          set(
            state => ({
              currentStep: Math.min(TOTAL_STEPS, state.currentStep + 1),
            }),
            false,
            'onboarding/nextStep'
          ),

        prevStep: () =>
          set(
            state => ({
              currentStep: Math.max(1, state.currentStep - 1),
            }),
            false,
            'onboarding/prevStep'
          ),

        goToStep: step =>
          set(
            { currentStep: Math.max(1, Math.min(TOTAL_STEPS, step)) },
            false,
            'onboarding/goToStep'
          ),

        markStepCompleted: step =>
          set(
            state => ({
              completedSteps: state.completedSteps.includes(step)
                ? state.completedSteps
                : [...state.completedSteps, step],
            }),
            false,
            'onboarding/markStepCompleted'
          ),

        // Skipped steps
        addSkippedStep: step =>
          set(
            state => ({
              skippedSteps: state.skippedSteps.includes(step)
                ? state.skippedSteps
                : [...state.skippedSteps, step],
            }),
            false,
            'onboarding/addSkippedStep'
          ),

        removeSkippedStep: step =>
          set(
            state => ({
              skippedSteps: state.skippedSteps.filter(s => s !== step),
            }),
            false,
            'onboarding/removeSkippedStep'
          ),

        clearSkippedSteps: () => set({ skippedSteps: [] }, false, 'onboarding/clearSkippedSteps'),

        // Socket
        setSocketConnected: value =>
          set(
            state => ({ socket: { ...state.socket, isConnected: value } }),
            false,
            'onboarding/setSocketConnected'
          ),

        setSocketError: error =>
          set(
            state => ({ socket: { ...state.socket, connectionError: error } }),
            false,
            'onboarding/setSocketError'
          ),

        // Automation
        startAutomation: () =>
          set(
            {
              automation: {
                isRunning: true,
                currentStage: 'analyzing_niche',
                stages: initialAutomationStages.map(s => ({ ...s })),
                startedAt: new Date().toISOString(),
                error: null,
              },
            },
            false,
            'onboarding/startAutomation'
          ),

        // Flags an already-started run as live without touching stage data.
        // Only `startAutomation` ever set `isRunning`, and it resets every
        // stage — unusable for a run that began before this page load. Polling
        // calls this when the backend still reports work in flight so the
        // setup surfaces survive a mid-run reload (LIL-468).
        markAutomationRunning: () =>
          set(
            state => {
              const { isRunning, currentStage, startedAt, stages } = state.automation;
              const nextStage = currentStage ?? deriveCurrentStage(stages);
              // Idempotent: polling calls this on every tick of a live run.
              if (isRunning && startedAt && nextStage === currentStage) return {};
              return {
                automation: {
                  ...state.automation,
                  isRunning: true,
                  currentStage: nextStage,
                  // A local stamp only until the next poll hands us the
                  // backend's real start time (setAutomationStartedAt).
                  startedAt: startedAt ?? new Date().toISOString(),
                },
              };
            },
            false,
            'onboarding/markAutomationRunning'
          ),

        // The app seeds startAutomation() on entry, which stamps "now". Polling
        // then hands us the real backend start time so the elapsed counter
        // reflects the actual run instead of restarting on every refresh.
        setAutomationStartedAt: startedAt =>
          set(
            state => (startedAt ? { automation: { ...state.automation, startedAt } } : state),
            false,
            'onboarding/setAutomationStartedAt'
          ),

        updateAutomationStage: stageUpdate =>
          set(
            state => ({
              automation: {
                ...state.automation,
                currentStage:
                  stageUpdate.status === 'processing'
                    ? stageUpdate.stage
                    : state.automation.currentStage,
                stages: state.automation.stages.map(s =>
                  s.stage === stageUpdate.stage ? { ...s, ...stageUpdate } : s
                ),
              },
            }),
            false,
            'onboarding/updateAutomationStage'
          ),

        setAutomationError: error =>
          set(
            state => {
              const stages = state.automation.stages;
              // Prefer the explicit stage on the error payload. Fall back to the
              // currently-processing stage — the backend sometimes emits a
              // generic stage value (e.g. "automation") on aggregate failures.
              const knownStages = new Set<string>(stages.map(s => s.stage));
              const targetStage = knownStages.has(error.stage)
                ? error.stage
                : stages.find(s => s.status === 'processing')?.stage;
              return {
                automation: {
                  ...state.automation,
                  isRunning: false,
                  error,
                  stages: targetStage
                    ? stages.map(s => (s.stage === targetStage ? { ...s, status: 'error' } : s))
                    : stages,
                },
              };
            },
            false,
            'onboarding/setAutomationError'
          ),

        completeAutomation: () =>
          set(
            state => ({
              automation: {
                ...state.automation,
                isRunning: false,
                currentStage: null,
                startedAt: null,
                stages: state.automation.stages.map(s => ({
                  ...s,
                  status: s.status === 'error' ? s.status : 'completed',
                })),
              },
            }),
            false,
            'onboarding/completeAutomation'
          ),

        // Reset
        reset: () =>
          set(
            {
              ...initialState,
              automation: {
                ...initialState.automation,
                stages: initialAutomationStages.map(s => ({ ...s })),
              },
              socket: { isConnected: false, connectionError: null },
            },
            false,
            'onboarding/reset'
          ),
      }),
      {
        name: 'lili-onboarding-storage',
        partialize: state => ({
          niche: { selected: [...state.niche.selected], searchQuery: '' },
          platforms: state.platforms,
          goals: state.goals,
          files: {
            uploaded: state.files.uploaded,
            storageUsed: state.files.storageUsed,
            storageQuota: state.files.storageQuota,
          },
          content: state.content,
          currentStep: state.currentStep,
          completedSteps: state.completedSteps,
          skippedSteps: state.skippedSteps,
        }),
        merge: (persisted, current) => {
          const p = persisted as Partial<OnboardingState> | undefined;
          if (!p) return current;

          // Drop keys that no longer belong on the state (migration safety).
          // `urls` was removed in LIL-217; strip it so rehydrated state stays typed.
          const { urls: _staleUrls, ...rest } = p as Partial<OnboardingState> & {
            urls?: unknown;
          };

          // Strip unknown platform keys that the backend no longer accepts
          const validPlatformKeys = new Set<string>(VALID_PLATFORM_KEYS);
          const cleanPlatforms: PlatformUrls = {};
          if (rest.platforms) {
            for (const [k, v] of Object.entries(rest.platforms)) {
              const frontendKey = toFrontendKey(k);
              if (validPlatformKeys.has(frontendKey) && v) {
                cleanPlatforms[frontendKey as keyof PlatformUrls] = v;
              }
            }
          }

          // Keep any niche the backend would accept (library id or custom
          // text) and cap to 3 (LIL-426). Coerce defensively: a legacy cache may
          // hold a single string here instead of an array.
          const cleanNiche = { ...current.niche, ...rest.niche };
          const rawSelected = (cleanNiche as { selected?: unknown }).selected;
          const selectedArray = Array.isArray(rawSelected)
            ? rawSelected
            : typeof rawSelected === 'string'
              ? [rawSelected]
              : [];
          cleanNiche.selected = selectedArray
            .filter((v): v is string => typeof v === 'string')
            .map(v => v.trim())
            .filter(v => v && v.length <= NICHE_MAX_LENGTH)
            .slice(0, 3);

          return {
            ...current,
            ...rest,
            platforms: cleanPlatforms,
            niche: cleanNiche,
            goals: { ...current.goals, ...rest.goals },
            files: { ...current.files, ...rest.files },
            content: {
              ...current.content,
              ...rest.content,
              // Coerce defensively: a corrupted/legacy cache could carry a
              // non-array here, which would crash the topics step on .filter/.includes.
              selectedTopics: Array.isArray(rest.content?.selectedTopics)
                ? rest.content.selectedTopics
                : current.content.selectedTopics,
              mix: { ...current.content.mix, ...rest.content?.mix },
            },
            voiceNotes: current.voiceNotes,
          };
        },
        onRehydrateStorage: () => state => {
          if (state) {
            // Restore non-persisted fields that partialize excludes
            state.automation = {
              ...initialState.automation,
              stages: initialAutomationStages.map(s => ({ ...s })),
            };
            state.socket = { isConnected: false, connectionError: null };
          }
        },
      }
    ),
    { name: 'OnboardingStore', enabled: import.meta.env.DEV }
  )
);
